import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useUserAuth from '../../../../utils/useUserAuth';

import {
  TicketCard,
  ExpiredCardWrapper,
  CardInfoSection,
  HeaderRow,
  HeaderLeft,
  ClassTypeLabel,
  TicketTitle,
  TypeLabel,
  InfoRow,
  InfoLeft,
  InfoLabel,
  InfoValue,
  HighlightText,
  ExpiredLabel,
  UpcomingLabel,
} from './CourseTicketCardStyled';
import { CardButtonArea, CardButton } from './PassCardStyled';

const URGENT_DAYS_THRESHOLD = 10;

// 일반상품 카드 — 라커("locker") + 일반상품("product") 두 종류를 한 컴포넌트에서 처리.
// 둘 다 횟수 개념이 없어 잔여횟수 row는 생략하고 잔여일수만 우측 강조.
const ProductCard = ({ item, kind, isExpired, onPeriodChange, onStop }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canProductPeriodChange = useUserAuth('MEMBER_PRODUCT_PERIOD_CHANGE');
  const isLocker = kind === 'locker';
  const hasLockerNumber = isLocker && item.lockerNumber;
  // 자리 배정 여부는 branchLockerId 로만 판단한다 (lockerNumber 는 표시용).
  const isUnassignedLocker = isLocker && !item.branchLockerId;

  // PassCard/CourseTicketCard와 동일한 중지 상태 판정 — 중지 시작일이 미래면 '중지 예정'.
  const isStoppedStatus = item.status === 'STOPPED';
  const isStopScheduled =
    isStoppedStatus && item.stoppedDate && dayjs(item.stoppedDate).startOf('day').isAfter(dayjs().startOf('day'));
  const isStopped = isStoppedStatus && !isStopScheduled;

  // 라커 번호는 구역별로 중복될 수 있어 번호 + 구역명을 함께 넘겨 정확히 찾도록 한다.
  const handleLockerClick = () => {
    const params = new URLSearchParams({ lockerNumber: item.lockerNumber });
    if (item.lockerAreaName) params.set('lockerAreaName', item.lockerAreaName);
    navigate(`/locker?${params.toString()}`);
  };

  // 시작일이 미래면 '사용예정'으로 표시하고 잔여일수는 계산하지 않는다.
  const today = dayjs().startOf('day');
  const isUpcoming = item.startDate ? today.isBefore(dayjs(item.startDate).startOf('day')) : false;

  const daysRemaining =
    !isUpcoming && item.expireDate ? dayjs(item.expireDate).startOf('day').diff(today, 'day') : null;
  const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= URGENT_DAYS_THRESHOLD;

  const card = (
    <TicketCard isStopped={!isExpired && isStopped}>
      <CardInfoSection>
        {/* Row 1: (라커번호 또는 지점) + 상품명 | 종류 라벨 */}
        <HeaderRow>
          <HeaderLeft>
            <ClassTypeLabel
              isStopped={!isExpired && isStopped}
              clickable={hasLockerNumber}
              onClick={hasLockerNumber ? handleLockerClick : undefined}>
              {hasLockerNumber ? `${item.lockerNumber}번 라커` : isUnassignedLocker ? t('미배정') : item.branchName}
            </ClassTypeLabel>
            <TicketTitle isStopped={!isExpired && isStopped}>{item.productName}</TicketTitle>
          </HeaderLeft>
          <TypeLabel isStopped={!isExpired && isStopped} sx={isStopScheduled ? { color: '#EE735A' } : {}}>
            {!isExpired && isStopScheduled
              ? `중지 예정 (${dayjs(item.stoppedDate).format('M/D')}~)`
              : !isExpired && isStopped
                ? '중지'
                : isLocker
                  ? t('락커')
                  : t('일반상품')}
          </TypeLabel>
        </HeaderRow>

        {/* Row 3: 유효기간 + 잔여일수 */}
        <InfoRow>
          <InfoLeft>
            <InfoLabel isStopped={!isExpired && isStopped}>{t('유효기간')}</InfoLabel>
            <InfoValue isStopped={!isExpired && isStopped}>
              {item.startDate?.replaceAll('-', '.')} ~ {item.expireDate?.replaceAll('-', '.')}
            </InfoValue>
          </InfoLeft>
          {isExpired ? (
            <ExpiredLabel>{t('만료')}</ExpiredLabel>
          ) : isUpcoming ? (
            <UpcomingLabel>{t('사용예정')}</UpcomingLabel>
          ) : (
            daysRemaining !== null &&
            daysRemaining >= 0 && (
              <HighlightText isStopped={isStopped} urgent={isUrgent}>
                {daysRemaining}일 남음
              </HighlightText>
            )
          )}
        </InfoRow>
      </CardInfoSection>

      <CardButtonArea isStopped={!isExpired && isStopped}>
        <Tooltip title={!canProductPeriodChange ? '권한이 없습니다' : ''} arrow>
          <CardButton
            variant="text"
            isStopped={!isExpired && isStopped}
            onClick={canProductPeriodChange ? onPeriodChange : undefined}
            style={!canProductPeriodChange ? { opacity: 0.5, cursor: 'default' } : {}}>
            {t('기간 변경')}
          </CardButton>
        </Tooltip>
        {item.status !== 'INACTIVE' && (
          <CardButton
            variant="text"
            isStopped={!isExpired && isStopped}
            noBorderLeft
            disabled={isLocker && !item.stopTargetPassId}
            onClick={onStop}>
            {isLocker ? t('락커') : t('상품')} {t('중지 설정')}
          </CardButton>
        )}
      </CardButtonArea>
    </TicketCard>
  );

  if (isExpired) {
    return <ExpiredCardWrapper>{card}</ExpiredCardWrapper>;
  }

  return <Box>{card}</Box>;
};

export default ProductCard;
